import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  ArrowBack,
  Edit,
  Download,
  Person,
  TableChart,
  Timeline as TimelineIcon,
  Refresh,
  Visibility,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "context/PageTitleContext";
import api from "services/api";
import { checkPermission } from "services/permission.service";
import { PERMISSIONS } from "auth/permissionCodes";
import {
  getContractTeamMembers,
  getContractStatusHistory,
  getContractHistory,
  getContractHistorySummary,
  getContractDocuments,
  fetchClients,
  fetchContractors,
  getContractDocumentForPreview,
} from "../../services/contract.service";
import { fetchPersonnel } from "modules/systems/services/personnel/personnel.service";
import ContractItemHistory from "./ContractItemHistory";
import { formatCurrencyWithUnit, formatDate, formatTime } from "utils/formatters";
import AddContractModal from "./AddContractModal";
import ContractItemsTable from "./ContractItemsTable";
import PaymentManagement from "../payments/PaymentManagement";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import "./ContractDetail.css";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { AppTab, AppTabPanel } from "components/common/tabs";
import { AppInfoField, AppInfoGrid, AppSectionCard } from "components/common/cards";
import { AppButton } from "components/common/buttons";
import DataTable from "components/common/tables/DataTable";

const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    const statusLower = status ? status.toLowerCase() : "";
    const colors = {
      "chưa bắt đầu": "#ffd700",
      "đang thực hiện": "#4caf50",
      "hoàn thành": "#2196f3",
      "tạm dừng": "#ff9800",
      "hủy bỏ": "#f44336",
    };
    return colors[statusLower] || "#9e9e9e";
  };

  return (
    <Chip
      label={status || "Chưa xác định"}
      sx={{
        backgroundColor: getStatusColor(status),
        color: "white",
        fontWeight: "bold",
      }}
    />
  );
};

const ContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();

  const [contract, setContract] = useState(null);
  const [contractPersonnels, setContractPersonnels] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [hasPaymentPermission, setHasPaymentPermission] = useState(null);
  const [hasHistoryPermission, setHasHistoryPermission] = useState(false);
  const [historyType, setHistoryType] = useState("contract"); // contract, contract-status, items
  const [contractHistory, setContractHistory] = useState([]); // Tất cả loại lịch sử
  const [contractStatusHistory, setContractStatusHistory] = useState([]); // Chỉ lịch sử status (compatibility)
  const [historyLoading, setHistoryLoading] = useState(false);
  const [statusHistoryLoading, setStatusHistoryLoading] = useState(false);
  const [statusViewType, setStatusViewType] = useState("table"); // table or timeline
  const [historyViewType, setHistoryViewType] = useState("table"); // table or timeline cho tất cả history
  const [historySummary, setHistorySummary] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for documents pagination
  const [documentsPage, setDocumentsPage] = useState(1);
  const [documentsPerPage, setDocumentsPerPage] = useState(5); // 5 documents per page

  // Permission Snackbar state
  const [permissionSnackbar, setPermissionSnackbar] = useState({
    open: false,
    message: "",
    severity: "warning",
  });

  // State cho preview file
  const [previewModal, setPreviewModal] = useState({
    open: false,
    file: null,
    fileName: "",
    fileType: "",
    fileBlob: null,
  });

  const tabs = [
    { label: "Thông tin chung", value: 0 },
    { label: "Nhân sự theo hợp đồng", value: 1 },
    { label: "Hạng mục hợp đồng", value: 2 },
    { label: "Thanh toán", value: 3 },
    { label: "Đính kèm", value: 4 },
    { label: "Lịch sử", value: 5 },
  ];

  const personnelColumns = useMemo(() => [
    {
      accessorKey: "displayOrder", header: "STT", size: 50,
      Cell: ({ row, cell }) => (
        cell.getValue() || row.index + 1
      ),
    },
    {
      accessorKey: "fullName", header: "Họ và tên", size: 200,
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
      Cell: ({ cell }) => (
        <Box display="flex" alignItems="center">
          <Person
            sx={{
              mr: 1,
              color: "primary.main",
              fontSize: "1.125rem",
            }}
          />
          {cell.getValue() || "Chưa xác định"}
        </Box>
      ),
    },
    {
      accessorKey: "title", header: "Chức vụ", size: 120,
      Cell: ({ cell }) => (
        cell.getValue() || "Chưa xác định"
      ),
    },
    {
      accessorKey: "departmentName", header: "Phòng ban", size: 140,
      Cell: ({ cell }) => (
        cell.getValue() || "Chưa xác định"
      ),
    },
    {
      accessorKey: "enterpriseName", header: "Xí nghiệp", size: 200,
      Cell: ({ cell }) => (
        cell.getValue() || "Chưa xác định"
      ),
    },
    {
      accessorKey: "roleInContract", header: "Vai trò trong dự án", size: 120,
      Cell: ({ cell }) => (
        <Chip
          label={
            cell.getValue() || "Chưa xác định"
          }
          variant="outlined"
          size="small"
          sx={{
            fontFamily: "Inter, sans-serif",
            fontSize: "0.75rem",
          }}
        />
      )
    },
  ], []);

  // Load contract details
  const loadContractDetails = useCallback(
    async (setLoadingState = false) => {
      try {
        if (setLoadingState) {
          setLoading(true);
        }
        const response = await api.get(`/contract/${contractId}`);

        if (response.data.success) {
          setContract(response.data.data);
        } else {
          setError(response.data.message || "Không thể tải thông tin hợp đồng");
        }
      } catch (err) {
        console.error("Error loading contract details:", err);
        setError("Lỗi khi tải thông tin hợp đồng");
      }
    },
    [contractId],
  );
  // Load contract personnel
  const loadContractPersonnels = useCallback(async () => {
    try {
      // Get team members (chỉ có userId và roleInContract)
      const teamMembers = await getContractTeamMembers(contractId);

      if (!Array.isArray(teamMembers) || teamMembers.length === 0) {
        setContractPersonnels([]);
        return;
      }

      // Get all personnel details
      const allPersonnelResponse = await fetchPersonnel(
        1000,
        1,
        "",
        "displayOrder",
        "ASC",
      );

      const allPersonnels = Array.isArray(allPersonnelResponse.items)
        ? allPersonnelResponse.items
        : [];

      // Combine team members with personnel details
      const combinedData = teamMembers.map((teamMember) => {
        // teamMember.userId contains the personnelId
        const personnelId = teamMember.userId || teamMember.personnelId;
        const personnelInfo = allPersonnels.find(
          (p) => p.personnelId === personnelId,
        );

        return {
          ...personnelInfo, // Personnel info (fullName, title, departmentName, enterpriseName, etc.)
          ...teamMember, // ContractTeam info (displayOrder, roleInContract, etc.) - this will override personnelInfo's displayOrder if exists
          roleInContract:
            teamMember.RoleInContract || teamMember.roleInContract,
          // Explicitly use displayOrder from ContractTeam
          displayOrder: teamMember.displayOrder || 0,
        };
      });

      // Sort by displayOrder from ContractTeam (ascending)
      const sortedData = combinedData.sort((a, b) => {
        const orderA = a.displayOrder || 0; // displayOrder from ContractTeam
        const orderB = b.displayOrder || 0;
        return orderA - orderB;
      });

      setContractPersonnels(sortedData);
    } catch (err) {
      console.error("Error loading contract personnel:", err);
      setContractPersonnels([]);
    }
  }, [contractId]);

  // Load contract documents
  const loadContractDocuments = useCallback(async () => {
    try {
      const docs = await getContractDocuments(contractId, 1000, 1);

      setDocuments(docs || []);
    } catch (err) {
      setDocuments([]);
    }
  }, [contractId]);

  // Load clients and contractors for name mapping
  const loadClientsAndContractors = useCallback(async () => {
    try {
      const [clientsResponse, contractorsResponse] = await Promise.all([
        fetchClients(),
        fetchContractors(),
      ]);
      setClients(clientsResponse || []);
      setContractors(contractorsResponse || []);
    } catch (err) {
      console.error("Error loading clients and contractors:", err);
      setClients([]);
      setContractors([]);
    }
  }, []);

  // Load tất cả loại lịch sử từ ContractHistory
  const loadContractHistory = useCallback(
    async (entityType = null) => {
      if (!contractId) return;

      setHistoryLoading(true);
      try {
        // Nếu entityType là "all", thì truyền null để lấy tất cả
        const filterType =
          entityType === "all" ? null : entityType?.toUpperCase();

        const response = await getContractHistory(
          contractId,
          filterType,
          1,
          100,
        );

        const history = response?.items || response?.data || response;
        setContractHistory(Array.isArray(history) ? history : []);

        // Cũng load summary
        try {
          const summaryResponse = await getContractHistorySummary(contractId);
          setHistorySummary(summaryResponse);
        } catch (summaryError) {
          console.error("Error loading history summary:", summaryError);
        }
      } catch (error) {
        console.error("Error loading contract history:", error);
        setContractHistory([]);
        setSnackbar({
          open: true,
          message: "Lỗi khi tải lịch sử hợp đồng",
          severity: "error",
        });
      } finally {
        setHistoryLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    const loadAllData = async () => {
      if (contractId) {
        try {
          setLoading(true);
          await Promise.all([
            loadContractDetails(false), // Don't set loading in individual function
            loadContractPersonnels(),
            loadContractDocuments(),
            loadClientsAndContractors(),
          ]);

          // Load default history (all types) for "contract" filter
          loadContractHistory("all");
        } finally {
          setLoading(false);
        }
      }
    };

    loadAllData();
  }, [
    contractId,
    loadContractDetails,
    loadContractPersonnels,
    loadContractDocuments,
    loadContractHistory,
    loadClientsAndContractors,
  ]);

  // Set tiêu đề mặc định khi component mount
  useEffect(() => {
    setPageTitle("Chi tiết hợp đồng");
  }, [setPageTitle]);

  // Reset tiêu đề khi component unmount
  useEffect(() => {
    return () => {
      setPageTitle("Danh sách hợp đồng");
    };
  }, [setPageTitle]);

  // Kiểm tra quyền xem lịch sử khi component mount
  useEffect(() => {
    const checkHistoryPermission = async () => {
      try {
        const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_HISTORY_READ);
        setHasHistoryPermission(result.hasPermission);
      } catch (error) {
        console.error("Error checking history permission:", error);
        setHasHistoryPermission(false);
      }
    };

    checkHistoryPermission();
  }, []);

  // Reset historyType và reload history khi chuyển vào tab lịch sử
  useEffect(() => {
    if (currentTab === 5 && contractId && hasHistoryPermission) {
      // Reset về filter "Hợp đồng" mặc định
      setHistoryType("contract");
      // Load tất cả history
      loadContractHistory("all");
    }
  }, [currentTab, contractId, hasHistoryPermission, loadContractHistory]);

  const handleBack = () => {
    setPageTitle("Danh sách hợp đồng");
    navigate("/contracts");
  };

  const handleEdit = async () => {
    const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_UPDATE);
    if (result.hasPermission) {
      setEditModalOpen(true);
    } else {
      setPermissionSnackbar({
        open: true,
        message: "Bạn không có quyền cập nhật hợp đồng.",
        severity: "warning",
      });
    }
  };

  const handleEditModalClose = async () => {
    setEditModalOpen(false);

    try {
      setLoading(true);
      // Refresh data after edit
      await Promise.all([
        loadContractDetails(false), // Don't set loading in individual function
        loadContractPersonnels(),
        loadContractDocuments(),
        loadContractHistory(), // Refresh history to show new changes
      ]);

      setSnackbar({
        open: true,
        message: "Cập nhật hợp đồng thành công!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error refreshing data after edit:", error);
      setSnackbar({
        open: true,
        message: "Lỗi khi tải lại dữ liệu sau khi cập nhật!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = async (event, newValue) => {
    // Nếu đang chuyển sang tab thanh toán (tab 3)
    if (newValue === 3) {
      try {
        const result = await checkPermission(PERMISSIONS.CONTRACT.CONTRACT_PAYMENT_READ);
        setHasPaymentPermission(result.hasPermission);
      } catch (error) {
        console.error("Error checking payment permission:", error);
        setHasPaymentPermission(false);
      }
    }

    // Nếu đang chuyển sang tab lịch sử (tab 5), refresh lại dữ liệu
    if (newValue === 5) {
      try {
        await loadContractHistory();
      } catch (error) {
        console.error("Error refreshing history data:", error);
      }
    }

    setCurrentTab(newValue);
  };

  const handleHistoryTypeChange = (type) => {
    setHistoryType(type);
    if (type === "contract-status") {
      loadContractStatusHistory();
    } else if (type === "contract") {
      // Khi click "Hợp đồng", hiển thị tất cả loại history (contract + document + personnel)
      loadContractHistory("all");
    } else {
      loadContractHistory(type);
    }
  };

  const loadContractStatusHistory = async () => {
    if (!contractId) return;

    setStatusHistoryLoading(true);
    try {
      const response = await getContractStatusHistory(contractId);

      // API trả về object có structure: { success, message, data, totalCount }
      const history = response?.data || response;
      setContractStatusHistory(Array.isArray(history) ? history : []);
    } catch (error) {
      console.error("Error loading contract status history:", error);
      setContractStatusHistory([]);
      setSnackbar({
        open: true,
        message: "Lỗi khi tải lịch sử trạng thái hợp đồng",
        severity: "error",
      });
    } finally {
      setStatusHistoryLoading(false);
    }
  };

  // Group history data by date (not exact timestamp) for master-detail view
  const groupHistoryByTimestamp = (historyData) => {
    if (!Array.isArray(historyData) || historyData.length === 0) {
      return [];
    }

    // Group by date only (YYYY-MM-DD)
    const groupedData = historyData.reduce((groups, item) => {
      const date = new Date(item.changedAt);
      const dateKey = date.toISOString().split("T")[0]; // Get YYYY-MM-DD only
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {});

    // Convert to array and sort by date (newest first)
    const sortedGroups = Object.entries(groupedData)
      .sort(([a], [b]) => new Date(b) - new Date(a))
      .map(([dateKey, items]) => {
        // Sort items within the day by time (newest first)
        const sortedItems = items.sort(
          (a, b) => new Date(b.changedAt) - new Date(a.changedAt),
        );

        // Create master row
        const masterRow = {
          id: `group_${dateKey}`,
          isGroup: true,
          dateKey,
          changeCount: sortedItems.length,
          changedAt: dateKey, // Use date for display
          entityTypes: [...new Set(sortedItems.map((item) => item.entityType))],
          changeTypes: [...new Set(sortedItems.map((item) => item.changeType))],
          subRows: sortedItems.map((item, index) => ({
            ...item,
            id: `${dateKey}_${index}`,
            isGroup: false,
          })),
        };

        return masterRow;
      });

    return sortedGroups;
  };

  // Function để xem trước file
  const handlePreviewDocument = async (doc, isSavedDoc = false) => {
    const previewData = await openDocumentPreviewInNewTab({
      file: doc,
      isRemote: isSavedDoc,
      downloadFn: getContractDocumentForPreview,
      onAlert: setSnackbar,
    });
  };

  const handleDownloadDocument = useCallback(async (docId, documentName) => {
    try {
      const response = await api.get(`/contract-documents/download/${docId}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", documentName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading document:", err);
      setSnackbar({
        open: true,
        message: "Lỗi khi tải xuống tài liệu",
        severity: "error",
      });
    }
  }, []);

  // Sắp xếp documents theo loại file
  const sortedDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      const typeA = (
        a.documentType ||
        a.documentTypeName ||
        a.typeName ||
        a.type ||
        "Loại tài liệu"
      ).toLowerCase();
      const typeB = (
        b.documentType ||
        b.documentTypeName ||
        b.typeName ||
        b.type ||
        "Loại tài liệu"
      ).toLowerCase();
      return typeA.localeCompare(typeB, "vi", { numeric: true });
    });
  }, [documents]);

  // Paginated documents for current page
  const paginatedDocuments = useMemo(() => {
    const startIndex = (documentsPage - 1) * documentsPerPage;
    const endIndex = startIndex + documentsPerPage;
    return sortedDocuments.slice(startIndex, endIndex);
  }, [sortedDocuments, documentsPage, documentsPerPage]);

  // Columns configuration for document table
  const documentColumns = useMemo(
    () => [
      {
        accessorKey: "documentName",
        header: "Tên file",
        size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.documentName || row.original.name || "Tài liệu"}
          </Typography>
        ),
      },
      {
        accessorKey: "documentType",
        header: "Loại file",
        size: 150,
        Cell: ({ row }) => (
          <Chip
            label={
              row.original.documentType ||
              row.original.documentTypeName ||
              row.original.typeName ||
              row.original.type ||
              "Loại tài liệu"
            }
            variant="outlined"
            size="small"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.75rem",
            }}
          />
        ),
      },
      {
        accessorKey: "description",
        header: "Mô tả",
        size: 200,
        muiTableBodyCellProps: () => {
          return {
            align: 'left',
          };
        },
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original.description || ""}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "Hành động",
        size: 120,
        Cell: ({ row }) => (
          <Stack
            direction="row"
            spacing={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip title="Xem trước">
              <AppButton
                variant="outlined"
                startIcon={<Visibility />}
                onClick={() => handlePreviewDocument(row.original, true)}
              >
                Xem trước
              </AppButton>
            </Tooltip>

            <Tooltip title="Tải xuống">
              <AppButton
                variant="outlined"
                startIcon={<Download />}
                onClick={() =>
                  handleDownloadDocument(
                    row.original.documentId || row.original.id,
                    row.original.documentName ||
                    row.original.name ||
                    "document",
                  )
                }
              >
                Tải về
              </AppButton>
            </Tooltip>
          </Stack>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [handleDownloadDocument],
  );

  // Total pages for documents
  const totalDocumentPages = Math.ceil(
    sortedDocuments.length / documentsPerPage,
  );

  // Debug logging for pagination

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Đang tải thông tin hợp đồng...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Lỗi</Typography>
          <Typography>{error}</Typography>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Quay lại danh sách
          </Button>
        </Alert>
      </Box>
    );
  }

  if (!contract) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Alert severity="warning" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Không tìm thấy hợp đồng</Typography>
          <Button onClick={handleBack} sx={{ mt: 2 }}>
            Quay lại danh sách
          </Button>
        </Alert>
      </Box>
    );
  }

  // Helper functions to convert ID to name
  const getClientNameById = (clientId) => {
    if (!clientId) return "";
    const client = clients.find((c) => c.clientId === parseInt(clientId));
    return client ? client.clientName : clientId;
  };

  const getContractorNameById = (contractorId) => {
    if (!contractorId) return "";
    const contractor = contractors.find(
      (c) => c.contractorId === parseInt(contractorId),
    );
    return contractor ? contractor.contractorName : contractorId;
  };

  // Helper function to format history values based on field name
  const formatHistoryValue = (value, fieldName) => {
    if (!value) return "-";

    // Convert client ID to name
    if (fieldName === "ClientId" || fieldName === "clientId") {
      return getClientNameById(value);
    }

    // Convert contractor ID to name
    if (fieldName === "ContractorId" || fieldName === "contractorId") {
      return getContractorNameById(value);
    }

    return value;
  };

  return (
    <Box
      className="contract-detail-container"
      sx={{
        display: "flex",
        justifyContent: "center",
        py: 2,
        // px: 2,
        width: "98%",
      }}
    >
      {/* Single Paper Container */}
      <Paper
        className="contract-detail-paper"
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          fontFamily: "Inter, sans-serif",
          width: "100%",
          maxWidth: "none",
          flex: 1,
        }}
      >
        {/* Header Section inside Paper */}
        <Box className="contract-header-section"
          sx={{
            px: 3,
            py: 1,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <AppButton
                startIcon={<ArrowBack />}
                onClick={handleBack}
                sx={{
                  color: "text.primary",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Trở về danh sách
              </AppButton>
            </Box>
            <Box display="flex" alignItems="center" gap={2}>
              <StatusBadge status={contract.status} />
              <AppButton
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
              >
                Cập nhật hợp đồng
              </AppButton>
            </Box>
          </Box>
        </Box>

        {/* Main Content - Tabs */}
        <Box className="contract-content-tabs"
          sx={{
            px: 2,
            pb: 2
          }}
        >
          <AppTab
            value={currentTab}
            onChange={handleTabChange}
            tabs={tabs}
          />

          {/* Tab Content */}
          <Box sx={{ mt: 1 }}>
            <AppTabPanel value={currentTab} index={0}>
              {/* Tab 0: Thông tin chung */}
              {currentTab === 0 && (
                <Box sx={{
                  display: "grid",
                  gap: "16px",
                }}>
                  <AppSectionCard title="Thông tin cơ bản">
                    <AppInfoGrid columns={1}>
                      <AppInfoField label="Số hợp đồng" value={contract.contractNumber} />
                      <AppInfoField label="Tên hợp đồng" value={contract.contractName} />
                    </AppInfoGrid>
                    <AppInfoGrid
                      columns={3}
                      sx={{
                        mt: "16px"
                      }}
                    >
                      <AppInfoField label="Lĩnh vực hợp đồng" value={contract.contractGroup || "Chưa xác định"} />
                      <AppInfoField label="Chủ đầu tư" value={contract.clientName || "Chưa xác định"} />
                      <AppInfoField label="Đơn vị thi công" value={contract.contractorName || "Chưa xác định"} />
                      <AppInfoField label="Giá trị hợp đồng" value={formatCurrencyWithUnit(contract.totalValue)} />
                      <AppInfoField label="VAT"
                        value={contract.vat !== null && contract.vat !== undefined
                          ? `${contract.vat}%`
                          : "Chưa xác định"}
                      />
                      <AppInfoField label="Phạm vi công việc" value={contract.scope || "Chưa xác định"} />
                      <Box className="subcontract-info-field" sx={{ gridColumn: "1 / -1" }}><label>Mô tả</label><strong>{contract.description || "Chưa có mô tả"}</strong></Box>
                    </AppInfoGrid>
                  </AppSectionCard>

                  <AppSectionCard title="Thời gian">
                    <AppInfoGrid columns={3}>
                      <AppInfoField label="Ngày ký hợp đồng" value={formatDate(contract.signDate)} />
                      <AppInfoField label="Ngày bắt đầu theo hợp đồng" value={formatDate(contract.startDate)} />
                      <AppInfoField label="Ngày kết thúc theo hợp đồng" value={formatDate(contract.endDate)} />
                      <AppInfoField label="Ngày bắt đầu thực tế" value={formatDate(contract.startDateAct) || "Chưa bắt đầu"} />
                      <AppInfoField label="Ngày kết thúc thực tế" value={formatDate(contract.endDateAct) || "Chưa hoàn thành"} />
                    </AppInfoGrid>
                  </AppSectionCard>
                </Box>
              )}
            </AppTabPanel>

            <AppTabPanel value={currentTab} index={1}>
              {/* Tab 1: Nhân sự theo hợp đồng */}
              {currentTab === 1 && (
                <Box>
                  {contractPersonnels.length > 0 ? (
                    <DataTable columns={personnelColumns} data={contractPersonnels.filter((item) => item.selected !== false)} />
                  ) : (
                    <Alert
                      severity="info"
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.875rem",
                      }}
                    >
                      Chưa có nhân sự nào được phân công cho hợp đồng này
                    </Alert>
                  )}
                </Box>
              )}
            </AppTabPanel>

            <AppTabPanel value={currentTab} index={2}>
              {/* Tab 2: Hạng mục hợp đồng */}
              {currentTab === 2 && (
                <Box>
                  <ContractItemsTable
                    contractId={contractId}
                    contract={contract}
                    onSnackbar={(message, severity) =>
                      setSnackbar({ open: true, message, severity })
                    }
                  />
                </Box>
              )}
            </AppTabPanel>

            <AppTabPanel value={currentTab} index={3}>
              {/* Tab 3: Thanh toán */}
              {currentTab === 3 && (
                <Box>
                  {hasPaymentPermission === null ? (
                    // Đang kiểm tra quyền
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="200px"
                    >
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "Inter, sans-serif" }}
                      >
                        Đang kiểm tra quyền truy cập...
                      </Typography>
                    </Box>
                  ) : hasPaymentPermission ? (
                    // Có quyền - hiển thị PaymentManagement
                    <PaymentManagement
                      contractId={contractId}
                      contractData={contract}
                    />
                  ) : (
                    // Không có quyền - hiển thị thông báo
                    <Alert
                      severity="warning"
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.875rem",
                      }}
                    >
                      Bạn không có quyền xem thông tin thanh toán
                    </Alert>
                  )}
                </Box>
              )}
            </AppTabPanel>

            <AppTabPanel value={currentTab} index={4}>
              {/* Tab 4: Đính kèm */}
              {currentTab === 4 && (
                <Box>
                  {sortedDocuments.length > 0 ? (
                    <DataTable
                      columns={documentColumns}
                      data={paginatedDocuments}
                      manualPagination={true}                 // 1. Báo cho DataTable biết dữ liệu đưa vào đã được phân trang sẵn
                      rowCount={sortedDocuments.length}       // 2. Báo tổng số bản ghi thực tế để bảng tính đúng số trang
                      state={{
                        pagination: {
                          pageIndex: documentsPage - 1,       // 3. Trang hiện tại (0-based index)
                          pageSize: documentsPerPage,         // 4. Số dòng trên mỗi trang
                        },
                      }}
                      onPaginationChange={(updater) => {      // 5. Cập nhật lại state khi người dùng bấm chuyển trang / đổi số dòng
                        const nextPagination =
                          typeof updater === "function"
                            ? updater({
                              pageIndex: documentsPage - 1,
                              pageSize: documentsPerPage,
                            })
                            : updater;

                        setDocumentsPage(nextPagination.pageIndex + 1);
                        setDocumentsPerPage(nextPagination.pageSize);
                      }}
                      rowsPerPageOptions={[5, 10, 20]}
                    />
                  ) : (
                    <Alert
                      severity="info"
                      sx={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.875rem",
                      }}
                    >
                      Chưa có tài liệu nào được đính kèm
                    </Alert>
                  )}
                </Box>
              )}
            </AppTabPanel>

            <AppTabPanel value={currentTab} index={5}>
              {/* Tab 5: Lịch sử */}
              {currentTab === 5 && hasHistoryPermission && (
                <Box>
                  {/* History Type Navigation */}
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        overflowX: "auto",
                        pb: 1,
                        "&::-webkit-scrollbar": {
                          height: 6,
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: 3,
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#c1c1c1",
                          borderRadius: 3,
                          "&:hover": {
                            backgroundColor: "#a8a8a8",
                          },
                        },
                      }}
                    >
                      <Button
                        variant={
                          historyType === "contract" ? "contained" : "outlined"
                        }
                        onClick={() => handleHistoryTypeChange("contract")}
                        sx={{
                          textTransform: "none",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.75rem",
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                          ...(historyType === "contract" && {
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          }),
                          ...(historyType !== "contract" && {
                            borderColor: "#e0e0e0",
                            color: "text.primary",
                            backgroundColor: "#f8f9fa",
                            "&:hover": {
                              backgroundColor: "#e9ecef",
                              borderColor: "#c1c1c1",
                            },
                          }),
                        }}
                      >
                        Hợp đồng
                      </Button>

                      <Button
                        variant={
                          historyType === "contract-status"
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => handleHistoryTypeChange("contract-status")}
                        sx={{
                          textTransform: "none",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.75rem",
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                          ...(historyType === "contract-status" && {
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          }),
                          ...(historyType !== "contract-status" && {
                            borderColor: "#e0e0e0",
                            color: "text.primary",
                            backgroundColor: "#f8f9fa",
                            "&:hover": {
                              backgroundColor: "#e9ecef",
                              borderColor: "#c1c1c1",
                            },
                          }),
                        }}
                      >
                        Trạng thái hợp đồng
                      </Button>

                      <Button
                        variant={
                          historyType === "items" ? "contained" : "outlined"
                        }
                        onClick={() => handleHistoryTypeChange("items")}
                        sx={{
                          textTransform: "none",
                          fontFamily: "Inter, sans-serif",
                          fontSize: "0.75rem",
                          borderRadius: 2,
                          px: 1.5,
                          py: 0.5,
                          minWidth: "auto",
                          whiteSpace: "nowrap",
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "none",
                          },
                          ...(historyType === "items" && {
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          }),
                          ...(historyType !== "items" && {
                            borderColor: "#e0e0e0",
                            color: "text.primary",
                            backgroundColor: "#f8f9fa",
                            "&:hover": {
                              backgroundColor: "#e9ecef",
                              borderColor: "#c1c1c1",
                            },
                          }),
                        }}
                      >
                        Hạng mục
                      </Button>
                    </Box>

                    {/* Toggle buttons for view type - only show for contract-status */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      {historyType === "contract-status" && (
                        <ToggleButtonGroup
                          value={statusViewType}
                          exclusive
                          onChange={(event, newViewType) => {
                            if (newViewType !== null) {
                              setStatusViewType(newViewType);
                            }
                          }}
                          size="small"
                          sx={{
                            "& .MuiToggleButton-root": {
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                              minWidth: "auto",
                            },
                          }}
                        >
                          <ToggleButton value="table" aria-label="table view">
                            <TableChart sx={{ mr: 0.5, fontSize: "1rem" }} />
                            Bảng
                          </ToggleButton>
                          <ToggleButton
                            value="timeline"
                            aria-label="timeline view"
                          >
                            <TimelineIcon sx={{ mr: 0.5, fontSize: "1rem" }} />
                            Timeline
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}

                      {/* Refresh button */}
                      <AppButton
                        variant="outlined"
                        onClick={() => {
                          if (historyType === "contract-status") {
                            loadContractStatusHistory();
                          } else if (historyType === "contract") {
                            // Khi refresh filter "Hợp đồng", load tất cả loại history
                            loadContractHistory("all");
                          } else {
                            loadContractHistory(historyType);
                          }
                        }}
                        disabled={historyLoading || statusHistoryLoading}
                        sx={{ color: "text.primary", }}
                      >
                        <Refresh sx={{ mr: 0.5, fontSize: "16px" }} />
                        Làm mới
                      </AppButton>
                    </Box>
                  </Box>

                  {/* History Content */}
                  <Box>
                    {(historyType === "contract" ||
                      historyType === "all" ||
                      historyType === "document" ||
                      historyType === "personnel") && (
                        <Box>
                          {historyLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                              <CircularProgress />
                            </Box>
                          ) : !Array.isArray(contractHistory) ||
                            contractHistory.length === 0 ? (
                            <Alert severity="info">
                              Chưa có lịch sử thay đổi cho hợp đồng này.
                            </Alert>
                          ) : historyViewType === "table" ? (
                            // Table View
                            <MaterialReactTable
                              columns={[
                                {
                                  accessorKey: "changedAt",
                                  header: "Thời gian",
                                  size: 150,
                                  enableGlobalFilter: true,
                                  accessorFn: (row) => {
                                    if (row.isGroup) {
                                      return formatDate(row.changedAt);
                                    }
                                    return formatTime(row.changedAt);
                                  },
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;
                                    const date = cell.getValue();

                                    if (!date) return "-";

                                    // Master row - hiển thị ngày
                                    if (original.isGroup) {
                                      return (
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          {formatDate(original.changedAt)}
                                        </Typography>
                                      );
                                    }

                                    // Detail row - chỉ hiển thị giờ phút giây
                                    return (
                                      <Typography
                                        sx={{
                                          fontSize: "0.875rem",
                                          fontFamily: "Inter, sans-serif",
                                        }}
                                      >
                                        {new Date(date).toLocaleTimeString(
                                          "vi-VN",
                                          {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                          },
                                        )}
                                      </Typography>
                                    );
                                  },
                                },
                                {
                                  accessorKey: "changeType",
                                  header: "Loại thay đổi",
                                  size: 120,
                                  enableGlobalFilter: true,
                                  accessorFn: (row) => {
                                    if (row.isGroup) {
                                      return `${row.changeCount} thay đổi`;
                                    }
                                    // Return both original value and display name for search
                                    const entityType = row.entityType;
                                    const changeType = row.changeType;

                                    let displayName = changeType;
                                    if (entityType === "Document") {
                                      if (
                                        changeType === "UPLOAD" ||
                                        changeType === "CREATE"
                                      )
                                        displayName = "Tải lên tài liệu";
                                      if (changeType === "DELETE")
                                        displayName = "Xóa tài liệu";
                                    }
                                    if (entityType === "Personnel") {
                                      if (changeType === "CREATE")
                                        displayName = "Thêm nhân sự";
                                      if (changeType === "DELETE")
                                        displayName = "Xóa nhân sự";
                                    }
                                    if (changeType === "UPDATE")
                                      displayName = "Cập nhật";
                                    if (changeType === "CREATE")
                                      displayName = "Tạo mới";

                                    return `${changeType} ${displayName}`;
                                  },
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;

                                    // Master row - hiển thị số thay đổi
                                    if (original.isGroup) {
                                      return (
                                        <Typography
                                          variant="body2"
                                          sx={{ fontWeight: "bold" }}
                                        >
                                          {original.changeCount} thay đổi
                                        </Typography>
                                      );
                                    }

                                    // Detail row - hiển thị chi tiết như cũ
                                    const changeType = original.changeType; // Lấy từ original thay vì cell.getValue()
                                    const entityType = original.entityType;

                                    const getChangeTypeDisplay = (
                                      type,
                                      entityType,
                                    ) => {
                                      if (entityType === "Document") {
                                        if (type === "UPLOAD" || type === "CREATE")
                                          return "Tải lên tài liệu";
                                        if (type === "DELETE")
                                          return "Xóa tài liệu";
                                      }
                                      if (entityType === "Personnel") {
                                        if (type === "CREATE")
                                          return "Thêm nhân sự";
                                        if (type === "DELETE") return "Xóa nhân sự";
                                      }

                                      const types = {
                                        CREATE: "Tạo mới",
                                        UPDATE: "Cập nhật",
                                        DELETE: "Xóa",
                                        UPLOAD: "Tải lên",
                                        DOCUMENT_UPLOAD: "Tải tài liệu",
                                        DOCUMENT_DELETE: "Xóa tài liệu",
                                        PERSONNEL_ADD: "Thêm nhân sự",
                                        PERSONNEL_REMOVE: "Xóa nhân sự",
                                      };
                                      return types[type] || type;
                                    };

                                    const getChangeTypeColor = (type) => {
                                      const colors = {
                                        CREATE: "#4caf50",
                                        UPDATE: "#2196f3",
                                        DELETE: "#f44336",
                                        UPLOAD: "#ff9800",
                                        DOCUMENT_UPLOAD: "#ff9800",
                                        DOCUMENT_DELETE: "#f44336",
                                        PERSONNEL_ADD: "#9c27b0",
                                        PERSONNEL_REMOVE: "#f44336",
                                      };
                                      return colors[type] || "#9e9e9e";
                                    };

                                    return (
                                      <Chip
                                        label={getChangeTypeDisplay(
                                          changeType,
                                          entityType,
                                        )}
                                        size="small"
                                        sx={{
                                          backgroundColor:
                                            getChangeTypeColor(changeType),
                                          color: "white",
                                          fontWeight: "bold",
                                          fontSize: "0.75rem",
                                        }}
                                      />
                                    );
                                  },
                                },
                                {
                                  accessorKey: "fieldDisplayName",
                                  header: "Trường thay đổi",
                                  size: 150,
                                  Cell: ({ row }) => {
                                    const original = row.original;

                                    // Master row - không hiển thị gì
                                    if (original.isGroup) {
                                      return "";
                                    }

                                    // Detail row - hiển thị chi tiết như cũ
                                    const {
                                      fieldDisplayName,
                                      entityType,
                                      changeType,
                                    } = original;

                                    if (fieldDisplayName) {
                                      return fieldDisplayName;
                                    }

                                    if (entityType === "DOCUMENT") {
                                      return changeType === "DOCUMENT_UPLOAD"
                                        ? "Tài liệu"
                                        : "Tài liệu";
                                    }

                                    if (entityType === "PERSONNEL") {
                                      return "Nhân sự";
                                    }

                                    return "-";
                                  },
                                },
                                {
                                  accessorKey: "oldValue",
                                  header: "Giá trị cũ",
                                  size: 200,
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;

                                    // Master row - không hiển thị gì
                                    if (original.isGroup) {
                                      return "-";
                                    }

                                    // Detail row - hiển thị chi tiết như cũ với formatting
                                    const value = cell.getValue();
                                    const fieldName = original.fieldName || "";
                                    const formattedValue = formatHistoryValue(
                                      value,
                                      fieldName,
                                    );

                                    return formattedValue !== "-" ? (
                                      <Typography
                                        sx={{
                                          fontSize: "0.875rem",
                                          fontFamily: "Inter, sans-serif",
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          maxHeight: "100px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {formattedValue}
                                      </Typography>
                                    ) : (
                                      "-"
                                    );
                                  },
                                },
                                {
                                  accessorKey: "newValue",
                                  header: "Giá trị mới",
                                  size: 200,
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;

                                    // Master row - không hiển thị gì
                                    if (original.isGroup) {
                                      return "-";
                                    }

                                    // Detail row - hiển thị chi tiết như cũ với formatting
                                    const value = cell.getValue();
                                    const fieldName = original.fieldName || "";
                                    const formattedValue = formatHistoryValue(
                                      value,
                                      fieldName,
                                    );

                                    return formattedValue !== "-" ? (
                                      <Typography
                                        sx={{
                                          fontSize: "0.875rem",
                                          fontFamily: "Inter, sans-serif",
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          maxHeight: "100px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {formattedValue}
                                      </Typography>
                                    ) : (
                                      "-"
                                    );
                                  },
                                },
                                {
                                  accessorKey: "changedBy",
                                  header: "Người thay đổi",
                                  size: 120,
                                  enableGlobalFilter: true,
                                  accessorFn: (row) => {
                                    if (row.isGroup) {
                                      return "";
                                    }
                                    return row.changedBy || "";
                                  },
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;

                                    // Master row - không hiển thị
                                    if (original.isGroup) {
                                      return "";
                                    }

                                    // Detail row - hiển thị người thay đổi
                                    return cell.getValue() || "-";
                                  },
                                },
                                {
                                  accessorKey: "changeReason",
                                  header: "Lý do thay đổi",
                                  size: 200,
                                  enableGlobalFilter: true,
                                  accessorFn: (row) => {
                                    if (row.isGroup) {
                                      return "";
                                    }
                                    return row.changeReason || "";
                                  },
                                  Cell: ({ cell, row }) => {
                                    const original = row.original;

                                    // Master row - không hiển thị gì
                                    if (original.isGroup) {
                                      return "-";
                                    }

                                    // Detail row - hiển thị chi tiết như cũ
                                    const reason = cell.getValue();
                                    return reason ? (
                                      <Typography
                                        sx={{
                                          fontSize: "0.875rem",
                                          fontFamily: "Inter, sans-serif",
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          maxHeight: "100px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {reason}
                                      </Typography>
                                    ) : (
                                      "-"
                                    );
                                  },
                                },
                              ]}
                              data={groupHistoryByTimestamp(contractHistory)}
                              enableExpanding={true}
                              enableColumnFilters={true}
                              enableGlobalFilter={true}
                              globalFilterFn="includesString"
                              filterFromLeafRows={true}
                              enableSorting={true}
                              enablePagination={true}
                              enableDensityToggle={false}
                              enableFullScreenToggle={false}
                              enableHiding={false}
                              getSubRows={(row) => row.subRows}
                              positionToolbarAlertBanner="bottom"
                              muiTableProps={{
                                sx: {
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 2,
                                  fontFamily: "Inter, sans-serif",
                                },
                              }}
                              muiTableHeadCellProps={{
                                sx: {
                                  backgroundColor: "#f8f9fa",
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: "bold",
                                  fontSize: "0.875rem",
                                  borderBottom: "1px solid #e0e0e0",
                                },
                              }}
                              muiTableBodyCellProps={{
                                sx: {
                                  fontFamily: "Inter, sans-serif",
                                  fontSize: "0.875rem",
                                  borderBottom: "1px solid #f0f0f0",
                                  verticalAlign: "top",
                                  padding: "12px 16px",
                                },
                              }}
                              muiTableBodyRowProps={({ row }) => ({
                                sx: {
                                  height: "auto",
                                  // Master rows styling
                                  ...(row.original.isGroup && {
                                    backgroundColor: "#e3f2fd",
                                    fontWeight: "bold",
                                    "&:hover": {
                                      backgroundColor: "#bbdefb",
                                    },
                                    "& .MuiTableCell-root": {
                                      borderBottom: "2px solid #2196f3",
                                      fontWeight: "bold",
                                    },
                                  }),
                                  // Detail rows styling
                                  ...(!row.original.isGroup && {
                                    "&:hover": {
                                      backgroundColor: "#f8f9fa",
                                    },
                                  }),
                                  "& .MuiTableCell-root": {
                                    height: "auto",
                                    minHeight: "48px",
                                  },
                                },
                              })}
                              localization={{
                                actions: "Hành động",
                                and: "và",
                                cancel: "Hủy",
                                changeFilterMode: "Thay đổi chế độ lọc",
                                changeSearchMode: "Thay đổi chế độ tìm kiếm",
                                clearFilter: "Xóa bộ lọc",
                                clearSearch: "Xóa tìm kiếm",
                                clearSort: "Xóa sắp xếp",
                                clickToCopy: "Nhấp để sao chép",
                                collapse: "Thu gọn",
                                collapseAll: "Thu gọn tất cả",
                                columnActions: "Hành động cột",
                                copiedToClipboard: "Đã sao chép vào clipboard",
                                dropToGroupBy: "Kéo để nhóm theo {column}",
                                edit: "Chỉnh sửa",
                                expand: "Mở rộng",
                                expandAll: "Mở rộng tất cả",
                                filterArrIncludes: "Bao gồm",
                                filterArrIncludesAll: "Bao gồm tất cả",
                                filterArrIncludesSome: "Bao gồm một số",
                                filterBetween: "Giữa",
                                filterBetweenInclusive: "Giữa (bao gồm)",
                                filterByColumn: "Lọc theo {column}",
                                filterContains: "Chứa",
                                filterEmpty: "Trống",
                                filterEndsWith: "Kết thúc bằng",
                                filterEquals: "Bằng",
                                filterEqualsString: "Bằng",
                                filterFuzzy: "Mờ",
                                filterGreaterThan: "Lớn hơn",
                                filterGreaterThanOrEqualTo: "Lớn hơn hoặc bằng",
                                filterInNumberRange: "Trong khoảng",
                                filterIncludesString: "Bao gồm",
                                filterIncludesStringSensitive:
                                  "Bao gồm (phân biệt chữ hoa/thường)",
                                filterLessThan: "Nhỏ hơn",
                                filterLessThanOrEqualTo: "Nhỏ hơn hoặc bằng",
                                filterMode: "Chế độ lọc: {filterType}",
                                filterNotEmpty: "Không trống",
                                filterNotEquals: "Không bằng",
                                filterStartsWith: "Bắt đầu bằng",
                                filterWeakEquals: "Bằng",
                                filteringByColumn:
                                  "Lọc theo {column} - {filterType} - {filterValue}",
                                goToFirstPage: "Đi đến trang đầu",
                                goToLastPage: "Đi đến trang cuối",
                                goToNextPage: "Đi đến trang tiếp theo",
                                goToPreviousPage: "Đi đến trang trước",
                                grab: "Lấy",
                                groupByColumn: "Nhóm theo {column}",
                                groupedBy: "Được nhóm theo ",
                                hideAll: "Ẩn tất cả",
                                hideColumn: "Ẩn cột {column}",
                                max: "Tối đa",
                                min: "Tối thiểu",
                                move: "Di chuyển",
                                noRecordsToDisplay:
                                  "Không có bản ghi nào để hiển thị",
                                noResultsFound: "Không tìm thấy kết quả",
                                of: "của",
                                or: "hoặc",
                                pinToLeft: "Ghim sang trái",
                                pinToRight: "Ghim sang phải",
                                resetColumnSize: "Đặt lại kích thước cột",
                                resetOrder: "Đặt lại thứ tự",
                                rowActions: "Hành động hàng",
                                rowNumber: "#",
                                rowNumbers: "Số thứ tự hàng",
                                rowsPerPage: "Số hàng mỗi trang",
                                save: "Lưu",
                                search: "Tìm kiếm",
                                select: "Chọn",
                                selectedCountOfRowCountRowsSelected:
                                  "{selectedCount} trên {rowCount} hàng đã chọn",
                                showAll: "Hiện tất cả",
                                showAllColumns: "Hiện tất cả cột",
                                showHideColumns: "Hiện/Ẩn cột",
                                showHideFilters: "Hiện/Ẩn bộ lọc",
                                showHideSearch: "Hiện/Ẩn tìm kiếm",
                                sortByColumnAsc: "Sắp xếp theo {column} tăng dần",
                                sortByColumnDesc: "Sắp xếp theo {column} giảm dần",
                                sortedByColumnAsc:
                                  "Đã sắp xếp theo {column} tăng dần",
                                sortedByColumnDesc:
                                  "Đã sắp xếp theo {column} giảm dần",
                                thenBy: ", sau đó theo ",
                                toggleDensity: "Chuyển đổi mật độ",
                                toggleFullScreen: "Chuyển đổi toàn màn hình",
                                toggleSelectAll: "Chuyển đổi chọn tất cả",
                                toggleSelectRow: "Chuyển đổi chọn hàng",
                                toggleVisibility: "Chuyển đổi hiển thị",
                                ungroupByColumn: "Bỏ nhóm theo {column}",
                                unpin: "Bỏ ghim",
                                unpinAll: "Bỏ ghim tất cả",
                                unsorted: "Chưa sắp xếp",
                              }}
                              initialState={{
                                density: "compact",
                                sorting: [{ id: "changedAt", desc: true }],
                              }}
                            />
                          ) : (
                            // Timeline View
                            <Timeline position="right">
                              {Array.isArray(contractHistory) &&
                                contractHistory.map((history, index) => {
                                  const getChangeTypeDisplay = (
                                    type,
                                    entityType,
                                  ) => {
                                    // Context-aware display based on EntityType
                                    if (entityType === "Document") {
                                      if (type === "UPLOAD" || type === "CREATE")
                                        return "Tải lên tài liệu";
                                      if (type === "DELETE") return "Xóa tài liệu";
                                    }
                                    if (entityType === "Personnel") {
                                      if (type === "CREATE") return "Thêm nhân sự";
                                      if (type === "DELETE") return "Xóa nhân sự";
                                    }

                                    // Default mapping
                                    const types = {
                                      CREATE: "Tạo mới hợp đồng",
                                      UPDATE: "Cập nhật hợp đồng",
                                      DELETE: "Xóa hợp đồng",
                                      UPLOAD: "Tải lên tài liệu",
                                      DOCUMENT_UPLOAD: "Tải lên tài liệu",
                                      DOCUMENT_DELETE: "Xóa tài liệu",
                                      PERSONNEL_ADD: "Thêm nhân sự",
                                      PERSONNEL_REMOVE: "Xóa nhân sự",
                                    };
                                    return types[type] || type;
                                  };

                                  const getChangeTypeColor = (type) => {
                                    const colors = {
                                      CREATE: "#4caf50",
                                      UPDATE: "#2196f3",
                                      DELETE: "#f44336",
                                      UPLOAD: "#ff9800",
                                      DOCUMENT_UPLOAD: "#ff9800",
                                      DOCUMENT_DELETE: "#f44336",
                                      PERSONNEL_ADD: "#9c27b0",
                                      PERSONNEL_REMOVE: "#f44336",
                                    };
                                    return colors[type] || "#2196f3";
                                  };

                                  return (
                                    <TimelineItem key={index}>
                                      <TimelineOppositeContent
                                        sx={{
                                          m: "auto 0",
                                          fontFamily: "Inter, sans-serif",
                                          fontSize: "0.875rem",
                                          pr: 2,
                                          textAlign: "right",
                                          fontWeight: "bold",
                                        }}
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        <Box sx={{ textAlign: "right" }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: "bold",
                                              fontSize: "0.875rem",
                                            }}
                                          >
                                            {history.changedAt
                                              ? new Date(
                                                history.changedAt,
                                              ).toLocaleDateString("vi-VN", {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit",
                                              })
                                              : "Chưa xác định"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: "bold",
                                              fontSize: "0.8rem",
                                              color: "text.secondary",
                                            }}
                                          >
                                            {history.changedAt
                                              ? new Date(
                                                history.changedAt,
                                              ).toLocaleTimeString("vi-VN", {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                              })
                                              : ""}
                                          </Typography>
                                        </Box>
                                      </TimelineOppositeContent>
                                      <TimelineSeparator>
                                        <TimelineConnector />
                                        <TimelineDot
                                          sx={{
                                            bgcolor: getChangeTypeColor(
                                              history.changeType,
                                            ),
                                            width: 12,
                                            height: 12,
                                          }}
                                        />
                                        <TimelineConnector />
                                      </TimelineSeparator>
                                      <TimelineContent
                                        sx={{
                                          py: "12px",
                                          pl: 2,
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          component="span"
                                          sx={{
                                            fontFamily: "Inter, sans-serif",
                                            fontSize: "0.95rem",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {getChangeTypeDisplay(
                                            history.changeType,
                                            history.entityType,
                                          )}
                                        </Typography>
                                        <Box sx={{ mt: 1, mb: 1 }}>
                                          {/* Field name */}
                                          {history.fieldDisplayName && (
                                            <Typography
                                              sx={{
                                                fontFamily: "Inter, sans-serif",
                                                fontSize: "0.875rem",
                                                fontWeight: 600,
                                                color: "primary.main",
                                                mb: 0.5,
                                              }}
                                            >
                                              {history.fieldDisplayName}
                                            </Typography>
                                          )}

                                          {/* Old value -> New value */}
                                          {history.oldValue && history.newValue && (
                                            <Box sx={{ mb: 1 }}>
                                              <Typography
                                                sx={{
                                                  fontFamily: "Inter, sans-serif",
                                                  fontSize: "0.875rem",
                                                  color: "text.secondary",
                                                }}
                                              >
                                                <strong>Từ:</strong>{" "}
                                                {formatHistoryValue(
                                                  history.oldValue,
                                                  history.fieldName,
                                                )}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  fontFamily: "Inter, sans-serif",
                                                  fontSize: "0.875rem",
                                                  color: "text.secondary",
                                                }}
                                              >
                                                <strong>Thành:</strong>{" "}
                                                {formatHistoryValue(
                                                  history.newValue,
                                                  history.fieldName,
                                                )}
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Only new value */}
                                          {!history.oldValue &&
                                            history.newValue && (
                                              <Typography
                                                sx={{
                                                  fontFamily: "Inter, sans-serif",
                                                  fontSize: "0.875rem",
                                                  color: "text.secondary",
                                                  mb: 1,
                                                }}
                                              >
                                                {formatHistoryValue(
                                                  history.newValue,
                                                  history.fieldName,
                                                )}
                                              </Typography>
                                            )}

                                          {/* Change reason */}
                                          {history.changeReason && (
                                            <Typography
                                              sx={{
                                                fontFamily: "Inter, sans-serif",
                                                fontSize: "0.875rem",
                                                color: "text.secondary",
                                                fontStyle: "italic",
                                                mb: 0.5,
                                              }}
                                            >
                                              <strong>Lý do:</strong>{" "}
                                              {history.changeReason}
                                            </Typography>
                                          )}

                                          {/* Changed by */}
                                          <Typography
                                            sx={{
                                              fontFamily: "Inter, sans-serif",
                                              fontSize: "0.875rem",
                                              color: "text.secondary",
                                              mt: 0.5,
                                            }}
                                          >
                                            <strong>Người thay đổi:</strong>{" "}
                                            {history.changedBy}
                                          </Typography>
                                        </Box>
                                      </TimelineContent>
                                    </TimelineItem>
                                  );
                                })}
                            </Timeline>
                          )}
                        </Box>
                      )}

                    {historyType === "contract-status" && (
                      <Box>
                        {statusHistoryLoading ? (
                          <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                          </Box>
                        ) : !Array.isArray(contractStatusHistory) ||
                          contractStatusHistory.length === 0 ? (
                          <Alert severity="info">
                            Chưa có lịch sử thay đổi trạng thái cho hợp đồng này.
                          </Alert>
                        ) : statusViewType === "table" ? (
                          // Table View
                          <MaterialReactTable
                            columns={[
                              {
                                accessorKey: "oldStatus",
                                header: "Trạng thái cũ",
                                size: 150,
                                Cell: ({ cell }) => (
                                  <StatusBadge status={cell.getValue()} />
                                ),
                              },
                              {
                                accessorKey: "newStatus",
                                header: "Trạng thái mới",
                                size: 150,
                                Cell: ({ cell }) => (
                                  <StatusBadge status={cell.getValue()} />
                                ),
                              },
                              {
                                accessorKey: "changeReason",
                                header: "Lý do thay đổi",
                                size: 250,
                                Cell: ({ cell }) => {
                                  const reason = cell.getValue();
                                  return reason ? (
                                    <Typography
                                      sx={{
                                        fontSize: "0.875rem",
                                        fontFamily: "Inter, sans-serif",
                                        wordBreak: "break-word",
                                        whiteSpace: "pre-wrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        maxHeight: "100px",
                                        overflowY: "auto",
                                      }}
                                    >
                                      {reason}
                                    </Typography>
                                  ) : (
                                    "-"
                                  );
                                },
                              },
                              {
                                accessorKey: "changedAt",
                                header: "Thời gian",
                                size: 180,
                                Cell: ({ cell }) => formatDate(cell.getValue()),
                              },
                              {
                                accessorKey: "changedBy",
                                header: "Người thay đổi",
                                size: 150,
                              },
                            ]}
                            data={contractStatusHistory}
                            enableColumnFilters={false}
                            enableColumnActions={false}
                            enablePagination={true}
                            enableSorting={true}
                            enableBottomToolbar={true}
                            enableTopToolbar={true}
                            enableGlobalFilter={true}
                            localization={{
                              actions: "Hành động",
                              and: "và",
                              cancel: "Hủy",
                              changeFilterMode: "Thay đổi chế độ lọc",
                              changeSearchMode: "Thay đổi chế độ tìm kiếm",
                              clearFilter: "Xóa bộ lọc",
                              clearSearch: "Xóa tìm kiếm",
                              clearSort: "Xóa sắp xếp",
                              clickToCopy: "Nhấp để sao chép",
                              collapse: "Thu gọn",
                              collapseAll: "Thu gọn tất cả",
                              columnActions: "Hành động cột",
                              copiedToClipboard: "Đã sao chép vào clipboard",
                              dropToGroupBy: "Thả để nhóm theo {column}",
                              edit: "Chỉnh sửa",
                              expand: "Mở rộng",
                              expandAll: "Mở rộng tất cả",
                              filterArrIncludes: "Bao gồm",
                              filterArrIncludesAll: "Bao gồm tất cả",
                              filterArrIncludesSome: "Bao gồm một số",
                              filterBetween: "Giữa",
                              filterBetweenInclusive: "Giữa (bao gồm)",
                              filterByColumn: "Lọc theo {column}",
                              filterContains: "Chứa",
                              filterEmpty: "Trống",
                              filterEndsWith: "Kết thúc bằng",
                              filterEquals: "Bằng",
                              filterEqualsString: "Bằng",
                              filterFuzzy: "Mờ",
                              filterGreaterThan: "Lớn hơn",
                              filterGreaterThanOrEqualTo: "Lớn hơn hoặc bằng",
                              filterInNumberRange: "Trong khoảng",
                              filterIncludesString: "Bao gồm",
                              filterIncludesStringSensitive: "Bao gồm",
                              filterLessThan: "Nhỏ hơn",
                              filterLessThanOrEqualTo: "Nhỏ hơn hoặc bằng",
                              filterMode: "Chế độ lọc: {filterType}",
                              filterNotEmpty: "Không trống",
                              filterNotEquals: "Không bằng",
                              filterStartsWith: "Bắt đầu bằng",
                              filterWeakEquals: "Bằng",
                              filteringByColumn:
                                "Lọc theo {column} - {filterType} - {filterValue}",
                              goToFirstPage: "Đi đến trang đầu",
                              goToLastPage: "Đi đến trang cuối",
                              goToNextPage: "Đi đến trang tiếp theo",
                              goToPreviousPage: "Đi đến trang trước",
                              grab: "Nắm",
                              groupByColumn: "Nhóm theo {column}",
                              groupedBy: "Nhóm theo ",
                              hideAll: "Ẩn tất cả",
                              hideColumn: "Ẩn cột {column}",
                              max: "Tối đa",
                              min: "Tối thiểu",
                              move: "Di chuyển",
                              noRecordsToDisplay:
                                "Không có bản ghi nào để hiển thị",
                              noResultsFound: "Không tìm thấy kết quả",
                              of: "của",
                              or: "hoặc",
                              pinToLeft: "Ghim bên trái",
                              pinToRight: "Ghim bên phải",
                              resetColumnSize: "Đặt lại kích thước cột",
                              resetOrder: "Đặt lại thứ tự",
                              rowActions: "Hành động hàng",
                              rowNumber: "#",
                              rowNumbers: "Số thứ tự hàng",
                              rowsPerPage: "Số hàng mỗi trang",
                              save: "Lưu",
                              search: "Tìm kiếm",
                              select: "Chọn",
                              selectedCountOfRowCountRowsSelected:
                                "{selectedCount} trên {rowCount} hàng đã chọn",
                              showAll: "Hiện tất cả",
                              showAllColumns: "Hiện tất cả cột",
                              showHideColumns: "Hiện/Ẩn cột",
                              showHideFilters: "Hiện/Ẩn bộ lọc",
                              showHideSearch: "Hiện/Ẩn tìm kiếm",
                              sortByColumnAsc: "Sắp xếp theo {column} tăng dần",
                              sortByColumnDesc: "Sắp xếp theo {column} giảm dần",
                              sortedByColumnAsc:
                                "Đã sắp xếp theo {column} tăng dần",
                              sortedByColumnDesc:
                                "Đã sắp xếp theo {column} giảm dần",
                              thenBy: ", sau đó theo ",
                              toggleDensity: "Chuyển đổi mật độ",
                              toggleFullScreen: "Chuyển đổi toàn màn hình",
                              toggleSelectAll: "Chuyển đổi chọn tất cả",
                              toggleSelectRow: "Chuyển đổi chọn hàng",
                              toggleVisibility: "Chuyển đổi hiển thị",
                              ungroupByColumn: "Bỏ nhóm theo {column}",
                              unpin: "Bỏ ghim",
                              unpinAll: "Bỏ ghim tất cả",
                              unsorted: "Chưa sắp xếp",
                            }}
                            muiTableProps={{
                              sx: {
                                fontFamily: "Inter, sans-serif",
                              },
                            }}
                            muiTableBodyRowProps={{
                              sx: {
                                height: "auto",
                                "&:hover": {
                                  backgroundColor: "#f8f9fa",
                                },
                                "& .MuiTableCell-root": {
                                  height: "auto",
                                  minHeight: "48px",
                                },
                              },
                            }}
                            initialState={{
                              density: "compact",
                              sorting: [{ id: "changedAt", desc: true }],
                            }}
                          />
                        ) : (
                          // Timeline View
                          <Timeline position="right">
                            {Array.isArray(contractStatusHistory) &&
                              contractStatusHistory.map((history, index) => (
                                <TimelineItem key={index}>
                                  <TimelineOppositeContent
                                    sx={{
                                      m: "auto 0",
                                      fontFamily: "Inter, sans-serif",
                                      fontSize: "0.875rem",
                                      pr: 2,
                                      textAlign: "right",
                                      fontWeight: "bold",
                                    }}
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    <Box sx={{ textAlign: "right" }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        {history.changedAt
                                          ? new Date(
                                            history.changedAt,
                                          ).toLocaleDateString("vi-VN", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                          })
                                          : "Chưa xác định"}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "0.8rem",
                                          color: "text.secondary",
                                        }}
                                      >
                                        {history.changedAt
                                          ? new Date(
                                            history.changedAt,
                                          ).toLocaleTimeString("vi-VN", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                          })
                                          : ""}
                                      </Typography>
                                    </Box>
                                  </TimelineOppositeContent>
                                  <TimelineSeparator>
                                    <TimelineConnector />
                                    <TimelineDot
                                      sx={{
                                        bgcolor: "primary.main",
                                        width: 12,
                                        height: 12,
                                      }}
                                    />
                                    <TimelineConnector />
                                  </TimelineSeparator>
                                  <TimelineContent
                                    sx={{
                                      py: "12px",
                                      pl: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="h6"
                                      component="span"
                                      sx={{
                                        fontFamily: "Inter, sans-serif",
                                        fontSize: "0.95rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Thay đổi trạng thái
                                    </Typography>
                                    <Box sx={{ mt: 1, mb: 1 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: 1,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        <StatusBadge status={history.oldStatus} />
                                        <Typography
                                          sx={{
                                            fontFamily: "Inter, sans-serif",
                                            fontSize: "0.875rem",
                                            mx: 1,
                                          }}
                                        >
                                          →
                                        </Typography>
                                        <StatusBadge status={history.newStatus} />
                                      </Box>
                                      {history.changeReason && (
                                        <Typography
                                          sx={{
                                            fontFamily: "Inter, sans-serif",
                                            fontSize: "0.875rem",
                                            color: "text.secondary",
                                          }}
                                        >
                                          <strong>Lý do:</strong>{" "}
                                          {history.changeReason}
                                        </Typography>
                                      )}
                                      <Typography
                                        sx={{
                                          fontFamily: "Inter, sans-serif",
                                          fontSize: "0.875rem",
                                          color: "text.secondary",
                                          mt: 0.5,
                                        }}
                                      >
                                        <strong>Người thay đổi:</strong>{" "}
                                        {history.changedBy}
                                      </Typography>
                                    </Box>
                                  </TimelineContent>
                                </TimelineItem>
                              ))}
                          </Timeline>
                        )}
                      </Box>
                    )}

                    {historyType === "items" && (
                      <ContractItemHistory
                        contractId={parseInt(contractId)}
                        open={true}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </AppTabPanel>
          </Box>

        </Box>
      </Paper>

      {/* Edit Modal */}
      <AddContractModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onContractAdded={handleEditModalClose}
        editMode={true}
        contractId={contractId}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Permission Snackbar */}
      <Snackbar
        open={permissionSnackbar.open}
        autoHideDuration={3500}
        onClose={() =>
          setPermissionSnackbar({ ...permissionSnackbar, open: false })
        }
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setPermissionSnackbar({ ...permissionSnackbar, open: false })
          }
          severity={permissionSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {permissionSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContractDetail;
